import { Skeleton } from '@/components/ui/skeleton'
import { useIntegrations } from '@/hooks/useIntegrations'
import type { IntegrationHealthItem, IntegrationStatus } from '@/lib/api'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function timeAgo(iso: string | null): string {
  if (!iso) return 'NEVER'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'JUST NOW'
  if (mins < 60) return `${mins}M AGO`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}H AGO`
  return `${Math.floor(hours / 24)}D AGO`
}

const STATUS_META: Record<IntegrationStatus, { label: string; dot: string; text: string }> = {
  healthy: { label: 'HEALTHY', dot: 'bg-primary', text: 'text-primary' },
  degraded: { label: 'DEGRADED', dot: 'bg-[#ffd080]', text: 'text-[#ffd080]' },
  down: { label: 'DOWN', dot: 'bg-cmd-error', text: 'text-cmd-error' },
}

function railColor(status: IntegrationStatus): string {
  return STATUS_META[status].dot
}

// ─── Integration card ─────────────────────────────────────────────────────────

function IntegrationCard({ item }: { item: IntegrationHealthItem }) {
  const meta = STATUS_META[item.status] ?? STATUS_META.healthy
  return (
    <div className="relative bg-cmd-container">
      <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${railColor(item.status)}`} />
      <div className="p-3.5 pl-4">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-sans text-sm font-semibold text-white truncate">{item.name}</h4>
          <span className={`flex items-center gap-1.5 shrink-0 font-mono text-[0.6rem] ${meta.text}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
          </span>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2">
          <Stat label="Response" value={item.responseTimeMs !== null ? `${item.responseTimeMs}ms` : '--'} />
          <Stat label="Errors 24h" value={String(item.errorCount24h)} />
          <Stat label="Checked" value={timeAgo(item.lastCheckAt)} />
        </div>

        {item.status !== 'healthy' && item.lastError && (
          <p className="mt-2.5 font-mono text-[0.6rem] text-cmd-outline truncate" title={item.lastError}>
            {item.lastError}
          </p>
        )}
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="font-label text-[0.55rem] tracking-widest text-cmd-outline uppercase">{label}</p>
      <p className="font-mono text-xs font-medium text-white tabular-nums mt-0.5">{value}</p>
    </div>
  )
}

// ─── Grid ──────────────────────────────────────────────────────────────────────

export function IntegrationHealthGrid() {
  const { data, isLoading, error } = useIntegrations()
  const items = data?.data ?? []
  const summary = data?.summary

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h3 className="font-sans text-[0.7rem] font-semibold tracking-widest text-white uppercase">
            Integration Health
          </h3>
          <p className="font-label text-[0.6rem] text-cmd-outline">
            Hecate · external API + service reachability, every 15 minutes
          </p>
        </div>
        {summary && (
          <span className="font-mono text-[0.6rem] text-cmd-outline-variant tabular-nums">
            <span className="text-primary">{summary.healthy}</span>
            {' / '}
            <span className="text-[#ffd080]">{summary.degraded}</span>
            {' / '}
            <span className="text-cmd-error">{summary.down}</span>
            {' · '}
            {summary.total} total
          </span>
        )}
      </div>

      {error && (
        <div className="bg-cmd-error-container p-4">
          <p className="text-sm text-cmd-on-error font-label">FAULT DETECTED: {error.message}</p>
        </div>
      )}

      {isLoading ? (
        <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-cmd-container p-3.5 space-y-3">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-7 w-full" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="bg-cmd-container p-4">
          <p className="font-mono text-[0.65rem] text-cmd-outline uppercase">
            No integration telemetry yet — Hecate's first sweep has not run.
          </p>
        </div>
      ) : (
        <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <IntegrationCard key={item.slug} item={item} />
          ))}
        </div>
      )}
    </div>
  )
}
